// libraries
import { getActiveDeal } from '@makemydeal/dr-sales-checkin-store';

// consts/enums
import { DEAL_LOAD_FAILED_MESSAGE } from '../constants/applicationLoadErrorMessages';

// interfaces/types
import type { UpstreamDealError } from '../types/applicationLoadError.types';

/**
 * True when `activeDeal.details` holds an error body instead of a deal.
 *
 * The reducer assigns whatever the BFF returns in the deal slot without validating its shape, so
 * any upstream that answers with an error body in place of a deal ends up here. A real `DealXG`
 * always carries `dealId` and never a top-level `message` or `code` (see `DealXG` in
 * dr-shared-bff-types), so either an absent `dealId` or the presence of `message`/`code` identifies
 * an error, regardless of which upstream produced it or what else rides along in the same body. An
 * empty slot (`{}` — no deal on the session) is excluded explicitly, since it has none of those
 * fields either and must match nothing.
 *
 * `dealId` alone isn't a reliable "this is a real deal" signal: some upstream error bodies echo the
 * requested `dealXgId` back as `dealId` (e.g. cai-dealxg-api's `{ message: "The deal could not be
 * found", code: 14002, errors: [], id, dealId }`). That's why `message`/`code` presence is checked
 * independently of `dealId` rather than gating on `dealId`'s absence.
 *
 * The known cases are cai-dealxg-api answering an unresolvable `dealXgId` with HTTP 200 and
 * `{ message, code, errors }` rather than a 4xx — with or without the id echoed back.
 *
 * Not keyed on `applicationState`: `INVALID` also means "deal needs setup", which routes to Deal
 * Setup rather than an error page, and an error body can resolve to `MISSING_VEHICLE` instead.
 */
export const isUpstreamDealError = (state: any): boolean => {
    const deal = getActiveDeal(state) as UpstreamDealError;
    if (Object.keys(deal).length === 0) return false; // empty slot — no deal on the session

    return deal.dealId === undefined || deal.message !== undefined || deal.code !== undefined;
};

/** The upstream message, falling back to our own copy when the envelope carries no text. */
export const getUpstreamDealErrorMessage = (state: any): string =>
    (getActiveDeal(state) as UpstreamDealError).message || DEAL_LOAD_FAILED_MESSAGE;
